<template>
  <ExportButtons
    :csv="exportGridsToExcel"
    :pdf="exportGridsToPdf"
    :xlsx="exportGridsToExcel"
  />
  <div
    v-if="loaded"
    class="row"
  >
    <ScaleLoader v-if="loading" />
    <div v-else>
      <div class="vehicle-report-grid-card vehicle-report-grid-card--spaced">
        <DxDataGrid
          :ref="dataGridRef[0]"
          :allow-column-reordering="true"
          :allow-column-resizing="true"
          :column-auto-width="true"
          :column-hiding-enabled="true"
          class="vehicle-report-grid"
          :data-source="borderCrossingStore"
          :hover-state-enabled="true"
          :remote-operations="remoteOperations"
          :row-alternation-enabled="true"
          :show-borders="true"
          :show-column-lines="true"
          :show-row-lines="false"
        >
          <DxHeaderFilter :visible="false" />
          <DxPaging
            :page-index="0"
            :page-size="25"
          /> <!-- Shows the second page -->
          <DxPager
            :allowed-page-sizes="[10, 20, 25]"
            :show-info="true"
            :show-navigation-buttons="true"
            :show-page-size-selector="true"
          />
          <DxFilterRow :visible="true" />
          <DxColumnFixing :enabled="true" />
          <DxColumn :caption="$t('components.history.summary.dataGrids.borderCrossing')">
            <DxColumn
              :allow-filtering="true"
              :allow-header-filtering="false"
              :caption="$t('components.history.summary.dataGrids.fromCountry')"
              :filter-operations="allowedOperations"
              :customize-text="formatCountryCell"
              data-field="from_country"
            />
            <DxColumn
              :allow-filtering="true"
              :allow-header-filtering="false"
              :caption="$t('components.history.summary.dataGrids.toCountry')"
              :filter-operations="allowedOperations"
              :customize-text="formatCountryCell"
              data-field="to_country"
            />
            <DxColumn
              :allow-filtering="true"
              :allow-header-filtering="false"
              :caption="$t('components.history.summary.dataGrids.date')"
              :filter-operations="allowedOperations"
              :customize-text="formatTimestampCell"
              data-field="timestamp"
            />
            <DxColumn
              :allow-filtering="true"
              :allow-header-filtering="false"
              :caption="$t('components.history.summary.dataGrids.distance')"
              :filter-operations="allowedOperations"
              :customize-text="formatDistanceCell"
              data-field="distance"
            />
            <DxColumn
              :allow-filtering="true"
              :allow-header-filtering="false"
              :caption="$t('components.history.summary.dataGrids.place')"
              :filter-operations="allowedOperations"
              data-field="place"
            />
          </DxColumn>
        </DxDataGrid>
      </div>

      <div class="vehicle-report-grid-card vehicle-report-grid-card--spaced">
        <DxDataGrid
          :ref="dataGridRef[1]"
          class="vehicle-report-grid"
          :data-source="delegationStore"
          :remote-operations="remoteOperations"
          :row-alternation-enabled="true"
          :show-borders="true"
          :show-column-lines="true"
          :show-row-lines="false"
          style="margin-bottom: 0;"
        >
          <DxHeaderFilter :visible="false" />
          <DxPaging
            :page-index="0"
            :page-size="25"
          /> <!-- Shows the second page -->
          <DxPager
            :allowed-page-sizes="[10, 20, 25]"
            :show-info="true"
            :show-navigation-buttons="true"
            :show-page-size-selector="true"
          />
          <DxFilterRow :visible="true" />
          <DxColumnFixing :enabled="true" />
          <DxColumn :caption="$t('components.history.summary.dataGrids.workingTimeByCountry')">
            <DxColumn
              :allow-filtering="true"
              :allow-header-filtering="false"
              :caption="$t('components.history.summary.dataGrids.country')"
              :filter-operations="allowedOperations"
              :customize-text="formatCountryCell"
              data-field="country"
            />
            <DxColumn
              :allow-filtering="true"
              :allow-header-filtering="false"
              :caption="$t('components.history.summary.dataGrids.arrival')"
              :filter-operations="allowedOperations"
              :customize-text="formatTimestampCell"
              data-field="arrival_timestamp"
            />
            <DxColumn
              :allow-filtering="true"
              :allow-header-filtering="false"
              :caption="$t('components.history.summary.dataGrids.departure')"
              :filter-operations="allowedOperations"
              :customize-text="formatTimestampCell"
              data-field="departure_timestamp"
            />
            <DxColumn
              :allow-filtering="true"
              :allow-header-filtering="false"
              :caption="$t('components.history.summary.dataGrids.timeOfVisit')"
              :filter-operations="allowedOperations"
              :customize-text="formatSecondsCell"
              data-field="time"
            />
            <DxColumn
              :allow-filtering="true"
              :allow-header-filtering="false"
              :caption="$t('components.history.summary.dataGrids.driving')"
              :filter-operations="allowedOperations"
              :customize-text="formatMinutesCell"
              data-field="drive_time"
              width="150"
            />
            <DxColumn
              :allow-filtering="true"
              :allow-header-filtering="false"
              :caption="$t('components.history.summary.dataGrids.otherWork')"
              :filter-operations="allowedOperations"
              :customize-text="formatMinutesCell"
              data-field="work_time"
              width="150"
            />
            <DxColumn
              :allow-filtering="true"
              :allow-header-filtering="false"
              :caption="$t('components.history.summary.dataGrids.breakAndRest')"
              :filter-operations="allowedOperations"
              :customize-text="formatMinutesCell"
              data-field="rest_time"
              width="150"
            />
          </DxColumn>
        </DxDataGrid>
      </div>
      <h5 class="vehicle-report-grid-summary-title">
        {{ $t('components.history.summary.dataGrids.sum') }}
      </h5>
      <div class="vehicle-report-grid-card">
        <DxDataGrid
          :ref="dataGridRef[2]"
          :data-source="store.delegation_by_country_sum"
          class="vehicle-report-grid"
          :row-alternation-enabled="true"
          :show-borders="true"
          :show-column-headers="false"
          :show-column-lines="true"
          :show-row-lines="false"
        >
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :filter-operations="allowedOperations"
            data-field="country"
            :customize-text="formatCountryCell"
          />
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :filter-operations="allowedOperations"
            data-field="drive_time"
            :customize-text="formatMinutesCell"
            width="150"
          />
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :filter-operations="allowedOperations"
            data-field="work_time"
            :customize-text="formatMinutesCell"
            width="150"
          />
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :filter-operations="allowedOperations"
            data-field="rest_time"
            :customize-text="formatMinutesCell"
            width="150"
          />
        </DxDataGrid>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { getBorderCrossingReport } from '@/crud/getters'
import { getHumanDateTime, minutesToHoursMinutes, secondsToHoursMinutes } from '@/utils/tools'
import {
  DxColumn,
  DxColumnFixing,
  DxDataGrid,
  DxFilterRow,
  DxHeaderFilter,
  DxPager,
  DxPaging
} from 'devextreme-vue/data-grid'
import { Workbook } from 'exceljs'
import { saveAs } from 'file-saver'
import { exportDataGrid } from 'devextreme/excel_exporter'
import { jsPDF } from 'jspdf'
import 'jspdf-autotable'
import { exportDataGrid as exportDataGridToPdf } from 'devextreme/pdf_exporter'
import { importFont } from '@/utils/jspdf/importFont'
import ExportButtons from '@/components/content/_partials/ExportButtons.vue'
import ScaleLoader from 'vue-spinner/src/ScaleLoader.vue'
import CustomStore from 'devextreme/data/custom_store'
import { buildGridLoadParams, normalizeGridResponse } from '@/services/reports/grid-store'

export default defineComponent({
  name: 'BorderCrossing',
  components: {
    DxColumn,
    DxColumnFixing,
    DxDataGrid,
    DxFilterRow,
    DxHeaderFilter,
    DxPager,
    DxPaging,
    ExportButtons,
    ScaleLoader
  },
  props: {
    imei: BigInt as unknown as PropType<bigint>,
    dateFrom: String,
    dateTo: String,
    vehicle: Object,
    reportType: String
  },
  data(): {
    store: any,
    borderCrossingStore: any,
    delegationStore: any,
    hasCan: boolean,
    hasCustomName: boolean,
    delegationCountries: Array<any>,
    delegationTimes: Array<any>,
    loaded: boolean,
    allowedOperations: Array<string>,
    remoteOperations: boolean,
    dataGridRef: Array<string>,
    loading: boolean
  } {
    return {
      store: [],
      borderCrossingStore: null,
      delegationStore: null,
      hasCan: false,
      hasCustomName: false,
      delegationCountries: [],
      delegationTimes: [],
      loaded: false,
      allowedOperations: ['contains', '=', 'notcontains', '!=', '>', '>=', '<', '<='],
      remoteOperations: true,
      dataGridRef: ['border_crossing', 'delegation_by_country', 'delegation_by_country_sum'],
      loading: true
    }
  },
  computed: {
    borderCrossingGridInstance() {
      const self: any = this
      return self.$refs[this.dataGridRef[0]].instance
    },
    delegationByCountryGridInstance() {
      const self: any = this
      return self.$refs[this.dataGridRef[1]].instance
    },
    delegationByCountrySumGridInstance() {
      const self: any = this
      return self.$refs[this.dataGridRef[2]].instance
    }
  },
  methods: {
    exportGridsToExcel(extension: string) {
      const self: any = this
      const workbook = new Workbook()

      const borderCrossingSheet = workbook
          .addWorksheet(self.$t('components.history.summary.dataGrids.borderCrossing'))
      const workingTimeByCountrySheet = workbook
          .addWorksheet(self.$t('components.history.summary.dataGrids.workingTimeByCountry'))
      const workingTimeByCountrySumSheet = workbook
          .addWorksheet(self.$t('components.history.summary.dataGrids.sum'))

      exportDataGrid({
        worksheet: borderCrossingSheet,
        component: self.borderCrossingGridInstance
      })
          .then(() => exportDataGrid({
            worksheet: workingTimeByCountrySheet,
            component: self.delegationByCountryGridInstance
          }))
          .then(() => exportDataGrid({
            worksheet: workingTimeByCountrySumSheet,
            component: self.delegationByCountrySumGridInstance
          }))
          .then(() => {
            if (extension == 'csv')
              workbook.csv.writeBuffer().then((buffer) => {
                saveAs(new Blob([buffer], { type: 'application/octet-stream' }),
                    `Border_crossing_${ self.imei }.csv`)
              })
            else if (extension == 'xlsx')
              workbook.xlsx.writeBuffer().then((buffer) => {
                saveAs(new Blob([buffer], { type: 'application/octet-stream' }),
                    `Border_crossing_${ self.imei }.xlsx`)
              })
          })
    },
    exportGridsToPdf() {
      const doc = new jsPDF();
      importFont(doc);

      let currentY = 20; // Start position for the first table

      exportDataGridToPdf({
        jsPDFDocument: doc,
        component: this.borderCrossingGridInstance,
        topLeft: { x: 0, y: currentY }
      })
          .then(() => {
            currentY += 50; // Adjust this value based on the height of the table to avoid overlap
            return exportDataGridToPdf({
              jsPDFDocument: doc,
              component: this.delegationByCountryGridInstance,
              topLeft: { x: 0, y: currentY }
            });
          })
          .then(() => {
            currentY += 50; // Adjust this value based on the height of the table to avoid overlap
            return exportDataGridToPdf({
              jsPDFDocument: doc,
              component: this.delegationByCountrySumGridInstance,
              topLeft: { x: 0, y: currentY }
            });
          })
          .then(() => {
            doc.save(`Border_crossing_${this.imei}.pdf`);
          })
          .catch(error => {
            console.error("PDF generation error:", error);
          });
    },
    formatTimestampCell(cellInfo: any): string {
      return cellInfo.value ? getHumanDateTime(cellInfo.value) : ''
    },
    formatSecondsCell(cellInfo: any): string {
      return secondsToHoursMinutes(cellInfo.value || 0)
    },
    formatMinutesCell(cellInfo: any): string {
      return minutesToHoursMinutes(cellInfo.value || 0)
    },
    formatDistanceCell(cellInfo: any): string {
      return `${cellInfo.value || 0} km`
    },
    formatCountryCell(cellInfo: any): string {
      if (!cellInfo.value) return ''
      return this.$t('components.history.summary.countries.' + String(cellInfo.value).toLowerCase()) as string
    },
    updateSummary(payload: any): void {
      if (!payload?.delegation_by_country_sum || this.store.delegation_by_country_sum?.length) return
      this.store.delegation_by_country_sum = payload.delegation_by_country_sum
    },
    createGridStore(grid: string): any {
      return new CustomStore({
        load: (loadOptions: any) => getBorderCrossingReport(
            this.imei as bigint,
            this.dateFrom as string,
            this.dateTo as string,
            buildGridLoadParams(loadOptions, {grid})
        ).then((response) => {
          this.updateSummary(response.data)
          return normalizeGridResponse(response.data[grid])
        }).catch(() => {
          ;(this as any).$swal.fire({
            icon: 'warning',
            title: 'Oops...',
            text: this.$t('components.error')
          })
          return {data: [], totalCount: 0}
        })
      })
    }
  },
  mounted() {
    if (this.imei && this.dateFrom && this.dateTo) {
      this.store = {delegation_by_country_sum: []}
      this.borderCrossingStore = this.createGridStore('border_crossing')
      this.delegationStore = this.createGridStore('delegation_by_country')
      this.loaded = true
      this.loading = false
    }
  }
})
</script>
<style lang="scss" scoped src="@/assets/scss/report-grid.scss"></style>
